import { ref } from 'vue';

export function useInputTools(svgCanvasRef) {
  const zoomLevel = ref(1);
  const panX = ref(0);
  const panY = ref(0);
  const isDragging = ref(false);
  const dragStartX = ref(0);
  const dragStartY = ref(0);
  const dragStartPanX = ref(0);
  const dragStartPanY = ref(0);

  const penToolActive = ref(false);
  const dragHandActive = ref(true);
  const drawingOverlay = ref(null);
  const isDrawing = ref(false);
  const drawnPaths = ref([]);
  const currentPath = ref('');
  const currentPathPoints = ref([]);
  const penColor = ref('#ff0000');
  const penStrokeWidth = ref(3);

  function getSVGCoordinates(e) {
    const canvas = svgCanvasRef.value?.svgCanvas;
    if (!canvas) return null;

    const ctm = canvas.getScreenCTM();
    if (!ctm) return null;

    const point = canvas.createSVGPoint();
    point.x = e.clientX;
    point.y = e.clientY;

    const svgPoint = point.matrixTransform(ctm.inverse());

    return {
      x: (svgPoint.x - panX.value) / zoomLevel.value,
      y: (svgPoint.y - panY.value) / zoomLevel.value,
    };
  }

  function handleMouseDown(e) {
    if (!dragHandActive.value || penToolActive.value) return;
    if (e.button !== 0) return;
    isDragging.value = true;
    dragStartX.value = e.clientX;
    dragStartY.value = e.clientY;
    dragStartPanX.value = panX.value;
    dragStartPanY.value = panY.value;
    e.preventDefault();
  }

  function handleMouseMove(e) {
    if (!dragHandActive.value || penToolActive.value) return;
    if (!isDragging.value) return;

    const deltaX = e.clientX - dragStartX.value;
    const deltaY = e.clientY - dragStartY.value;

    panX.value = dragStartPanX.value + deltaX;
    panY.value = dragStartPanY.value + deltaY;

    e.preventDefault();
  }

  function handleMouseUp(e) {
    if (!dragHandActive.value || penToolActive.value) return;
    isDragging.value = false;
  }

  function handleWheel(e) {
    e.preventDefault();

    const svg = svgCanvasRef.value?.svgCanvas;
    if (!svg) return;

    const ctm = svg.getScreenCTM();
    if (!ctm) return;

    const p = svg.createSVGPoint();
    p.x = e.clientX;
    p.y = e.clientY;

    const before = p.matrixTransform(ctm.inverse());

    const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(0.75, Math.min(5, zoomLevel.value * zoomFactor));
    const zoomChange = newZoom / zoomLevel.value;

    panX.value = before.x - (before.x - panX.value) * zoomChange;
    panY.value = before.y - (before.y - panY.value) * zoomChange;

    zoomLevel.value = newZoom;
  }

  function resetZoom() {
    zoomLevel.value = 1;
    panX.value = 0;
    panY.value = 0;
  }

  function toggleDragHand() {
    dragHandActive.value = !dragHandActive.value;
    if (dragHandActive.value) {
      penToolActive.value = false;
      if (isDrawing.value) {
        finishCurrentPath();
      }
    }
  }

  function togglePenTool() {
    penToolActive.value = !penToolActive.value;
    if (penToolActive.value) {
      dragHandActive.value = false;
    } else if (isDrawing.value) {
      finishCurrentPath();
    }
  }

  function clearDrawings() {
    drawnPaths.value = [];
    currentPath.value = '';
    currentPathPoints.value = [];
  }

  function handleDrawingMouseDown(e) {
    if (!penToolActive.value || e.button !== 0) return;

    const coords = getSVGCoordinates(e);
    if (!coords) return;

    isDrawing.value = true;
    currentPathPoints.value = [{ x: coords.x, y: coords.y }];
    currentPath.value = `M ${coords.x} ${coords.y}`;
    e.preventDefault();
  }

  function handleDrawingMouseMove(e) {
    if (!penToolActive.value || !isDrawing.value) return;

    const coords = getSVGCoordinates(e);
    if (!coords) return;

    currentPathPoints.value.push({ x: coords.x, y: coords.y });

    let pathString = `M ${currentPathPoints.value[0].x} ${currentPathPoints.value[0].y}`;
    for (let i = 1; i < currentPathPoints.value.length; i++) {
      pathString += ` L ${currentPathPoints.value[i].x} ${currentPathPoints.value[i].y}`;
    }
    currentPath.value = pathString;

    e.preventDefault();
  }

  function handleDrawingMouseUp(e) {
    if (!penToolActive.value || !isDrawing.value) return;

    finishCurrentPath();
    e.preventDefault();
  }

  function finishCurrentPath() {
    if (currentPath.value && currentPathPoints.value.length > 1) {
      drawnPaths.value.push({
        d: currentPath.value,
        stroke: penColor.value,
        strokeWidth: penStrokeWidth.value,
      });
    }
    currentPath.value = '';
    currentPathPoints.value = [];
    isDrawing.value = false;
  }

  return {
    zoomLevel,
    panX,
    panY,
    isDragging,
    penToolActive,
    dragHandActive,
    drawingOverlay,
    isDrawing,
    drawnPaths,
    currentPath,
    currentPathPoints,
    penColor,
    penStrokeWidth,
    getSVGCoordinates,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleWheel,
    resetZoom,
    toggleDragHand,
    togglePenTool,
    clearDrawings,
    handleDrawingMouseDown,
    handleDrawingMouseMove,
    handleDrawingMouseUp,
    finishCurrentPath,
  };
}
